import findResult, {
  findMatch, findWithCodes, firstSelected, removeItemByCode,
} from '@/utils/resultsHelper';
import { readStates, saveStates } from '@/constants/lifecycle';
import { openEntityDialog, pagingWithResults } from '@/constants/states';
import { linkPrefixes, splitUrl } from '@/constants/urls';
import withDraft from '@/utils/draftHelper';
import { datasetSettingsCategories } from '../../settings/constants';
import { UserDefinedRestrictionTypes } from '../constants';

const datasetConditionContext = {
  'ADDUC:0005': {
    type: 'text',
    text: '',
    link: '',
    link_display_name: '',
    protocol: linkPrefixes.HTTPS,
  },
  'ADDUC:0006': { names: [] },
  'ADDUC:0007': { locations: [] },
};

const datasetConditionConfigurations = {
  'ADDUC:0001': {
    existing_workspace_condition: 'on',
  },
  'ADDUC:0005': {},
  'ADDUC:0006': { existing_workspace_condition: 'on' },
  'ADDUC:0007': { existing_workspace_condition: 'on' },
};

const UNIQUE_CONDITION_CODES = [
  'ADDUC:0001',
  'ADDUC:0006',
  'ADDUC:0007',
];

const extractContext = (context) => {
  if (context.type === UserDefinedRestrictionTypes.LINK) {
    return {
      type: context.type,
      text: context.text,
      link_display_name: context.link_display_name,
      ...splitUrl(context.link),
    };
  }

  return {
    ...context,
  };
};

export const newDatasetCondition = (code, datasetCondition = null) => ({
  selected: true,
  context: datasetCondition?.context
    ? {
      ...datasetConditionContext[code],
      ...extractContext(datasetCondition.context),
    }
    : datasetConditionContext[code],
  configuration: datasetCondition?.configuration
    ? datasetCondition.configuration
    : datasetConditionConfigurations[code],
  condition: {
    code,
    ...(datasetCondition?.condition ? { ...datasetCondition.condition } : {}),
  },
});

export const activeCondition = () => ({
  ...openEntityDialog,
  ...saveStates,
  advisory: false,
  items: [],
  isNew: false,
  isDirty: false,
  name: '',
});

export const activeConditions = () => ({
  ...readStates,
  code: null,
  name: '',
  items: [],
  open: false,
});

export const conditionsToDelete = (open = false, name = '', results = []) => ({ open, name, results });

const initialState = {
  activeCondition: activeCondition(),
  activeConditions: activeConditions(),
  datasetConditions: {
    open: {
      results: [],
    },
    deletion: conditionsToDelete(),
  },
  conditions: {
    ...pagingWithResults(),
    ...readStates,
  },
  attachments: {
    downloadsInProgress: [],
  },
};

const matchDatasetCondition = (mappedItem, mappedItems) => {
  const condition = findMatch(mappedItem.parent.conditions, mappedItem.item.id, 'id');
  if (condition) {
    mappedItems.conditions.push({ ...mappedItem, condition });
  }
};

const matchDatasetConditionParent = (datasetConditions, category, item, mappedItems) => {
  const parent = findResult(datasetConditions, category, item.code);
  if (parent) {
    const mappedItem = { parent, category, item };
    mappedItems.parents.push(mappedItem);
    matchDatasetCondition(mappedItem, mappedItems);
  }
};

export const mapItemsToDatasetConditions = ({ datasetConditions }, { items }) => {
  const codes = items.map(({ code }) => code);
  const categories = findWithCodes(datasetConditions, datasetSettingsCategories, codes);
  const mappedItems = { parents: [], conditions: [] };

  categories.forEach((category) => {
    items.forEach((item) => {
      matchDatasetConditionParent(datasetConditions, category, item, mappedItems);
    });
  });

  return mappedItems;
};

export const availableDatasetConditions = (datasetConditions, conditions, { items, category, code }) => {
  const existing = findResult(datasetConditions, category, code) || { conditions: [] };
  const datasetCondition = firstSelected(items) || { condition: { code: null } };
  const codes = existing.conditions
    .map(({ condition }) => condition.code)
    .filter((c) => c !== datasetCondition.condition.code && UNIQUE_CONDITION_CODES.includes(c));

  return {
    conditions: conditions.results.filter((c) => !codes.includes(c.code)),
    datasetCondition,
  };
};

export const findDatasetConditions = ({ datasetConditions }, { category, code }) => (
  findResult(datasetConditions, category, code)
);

export const findDatasetCondition = ({ datasetConditions }, { category, code, datasetConditionId }) => {
  const item = findDatasetConditions({ datasetConditions }, { category, code });
  return { item, condition: findMatch(item.conditions, datasetConditionId, 'id') };
};

export const closeDatasetConditionsState = withDraft((draft, { category, code }) => {
  const item = findDatasetConditions(draft, { category, code });
  if (item) {
    draft.datasetConditions[category].results = (
      removeItemByCode(draft.datasetConditions[category].results, item));
  }
});

export default initialState;
